import { UserClient } from "./apolloClient";
import gql from "graphql-tag";
import {
  CheckGraphQLErrorForCode,
  ThrowGraphQLError,
  ValidationErrorReasons,
} from "../graphql/errors";

const fragments = {
  userGroupFragment: gql`
    fragment UserGroup on UserGroup {
      id
      revision
      name
      email
      desc
      parentID
      children {
        id
        name
      }
    }
  `,
};

export interface CreateUserGroupInput {
  name: string;
  email: string;
  desc: string;
  // create the group as a child of this group
  parentID?: string;
}

// a child of a user group
export interface UserGroupChild {
  id: string;
  name: string;
}

export interface UserGroup {
  id: string;
  revision: number;
  name: string;
  email: string;
  desc: string;
  // The parent of the group, if it is a child group. Members of a parent
  // group can see the orders assigned to its child groups.
  parentID?: string | null;
  // The child groups of the group. Only present when a single group is read.
  children?: UserGroupChild[];
}

const CreateUserGroup = async ({
  name,
  email,
  desc,
  parentID,
}: CreateUserGroupInput): Promise<UserGroup> => {
  const createUserGroup = gql`
    mutation createUserGroup($input: CreateUserGroupInput!) {
      createUserGroup(input: $input) {
        group {
          ...UserGroup
        }
        clientMutationId
      }
    }
    ${fragments.userGroupFragment}
  `;

  try {
    const r = await UserClient.mutate({
      mutation: createUserGroup,
      variables: {
        input: {
          name: name,
          email: email,
          description: desc,
          parentID: parentID,
          clientMutationId: "",
        },
      },
    });
    return r.data.createUserGroup.group;
  } catch (e) {
    // e.g. the parent is itself a child group
    const reasons = ValidationErrorReasons(e);
    if (reasons.length > 0) {
      throw new Error(reasons.join(". "));
    }
    throw e;
  }
};

export interface GetUserGroupOutput {
  UserGroup?: UserGroup;
}

const GetUserGroup = async (id: string): Promise<GetUserGroupOutput> => {
  const userGroupQuery = gql`
    query UserGroup($id: ID!) {
      UserGroup(id: $id) {
        ...UserGroup
      }
    }
    ${fragments.userGroupFragment}
  `;

  const r = await UserClient.query({
    query: userGroupQuery,
    variables: {
      id: id,
    },
  });

  return { UserGroup: r.data.UserGroup };
};

export interface UpdateUserGroupInput {
  id: string;
  revision: number;
  name?: string;
  desc?: string;
  email?: string;
}

export interface UpdateUserGroupOutput {
  userGroup: UserGroup;
}

const UpdateUserGroup = async ({
  id,
  revision,
  name,
  desc,
  email,
}: UpdateUserGroupInput): Promise<UpdateUserGroupOutput> => {
  const updateUserGroup = gql`
    mutation updateUserGroup($input: UpdateUserGroupInput!) {
      updateUserGroup(input: $input) {
        group {
          ...UserGroup
        }
        clientMutationId
      }
    }
    ${fragments.userGroupFragment}
  `;

  try {
    const r = await UserClient.mutate({
      mutation: updateUserGroup,
      variables: {
        input: {
          id: id,
          revision: revision,
          clientMutationId: "",
          name: name,
          email: email,
          description: desc,
        },
      },
    });
    return { userGroup: r.data.updateUserGroup.group };
  } catch (e) {
    console.log(e);
    if (CheckGraphQLErrorForCode(e, "revision_error")) {
      throw new Error(
        "User group data has changed since loaded, please try again"
      );
    } else {
      throw new Error("Failed to save");
    }
  }
};

interface UserGroupFilter {
  name: string;
  value: string;
}

export interface GetUserGroupsInput {
  filters?: UserGroupFilter[];
  nextKey?: string;
}

export interface GetUserGroupsOutput {
  items: UserGroup[];
  nextKey?: string;
}

const GetUserGroups = async (
  p: GetUserGroupsInput
): Promise<GetUserGroupsOutput> => {
  const userGroups = gql`
    query UserGroups($input: GetUserGroupsInput!) {
      UserGroups(input: $input) {
        groups {
          id
          name
          email
          desc
          parentID
        }
        nextKey
      }
    }
  `;

  // define the query input
  const input = {
    maxResults: 10,
    nextKey: p.nextKey,
    filters: p.filters,
  };

  const r = await UserClient.query({
    query: userGroups,
    variables: { input: input },
  });

  return {
    items: r.data.UserGroups.groups,
    nextKey: r.data.UserGroups.nextKey,
  };
};

// GetAllUserGroups lists every user group, following the pagination to the end.
// For pickers and name lookups; needs the 'group_list' permission.
const GetAllUserGroups = async (): Promise<UserGroupChild[]> => {
  const groups: UserGroupChild[] = [];
  let nextKey: string | undefined = undefined;
  do {
    const r: GetUserGroupsOutput = await GetUserGroups({ nextKey });
    groups.push(...r.items.map((g) => ({ id: g.id, name: g.name })));
    nextKey = r.nextKey || undefined;
  } while (nextKey);
  return groups.sort((a, b) => a.name.localeCompare(b.name));
};

export interface SetUserGroupParentInput {
  id: string;
  revision: number;
  // the new parent group; leave undefined to clear the parent
  parentID?: string;
}

// Sets, changes or clears the parent of a user group.
const SetUserGroupParent = async ({
  id,
  revision,
  parentID,
}: SetUserGroupParentInput): Promise<UserGroup> => {
  const setUserGroupParent = gql`
    mutation setUserGroupParent($input: SetUserGroupParentInput!) {
      setUserGroupParent(input: $input) {
        group {
          ...UserGroup
        }
        clientMutationId
      }
    }
    ${fragments.userGroupFragment}
  `;

  try {
    const r = await UserClient.mutate({
      mutation: setUserGroupParent,
      variables: {
        input: {
          id: id,
          revision: revision,
          clientMutationId: "",
          parentID: parentID || null,
        },
      },
    });
    return r.data.setUserGroupParent.group;
  } catch (e) {
    console.log(e);
    throw new Error(
      ValidationErrorReasons(e).join(". ") || "Failed to set the parent group"
    );
  }
};

const DeleteUserGroup = async (id: string): Promise<void> => {
  const deleteUserGroup = gql`
    mutation deleteUserGroup($input: DeleteUserGroupInput!) {
      deleteUserGroup(input: $input) {
        clientMutationId
      }
    }
  `;

  try {
    await UserClient.mutate({
      mutation: deleteUserGroup,
      variables: {
        input: {
          id: id,
          clientMutationId: "",
        },
      },
    });
  } catch (e) {
    // e.g. the group still has members, child groups or orders
    const reasons = ValidationErrorReasons(e);
    if (reasons.length > 0) {
      throw new Error(reasons.join(". "));
    }
    throw e;
  }
};

const addUser = async (userId: string, groupId: string): Promise<void> => {
  const addUserToGroup = gql`
    mutation addUserToGroup($input: AddUserToGroupInput!) {
      addUserToGroup(input: $input) {
        clientMutationId
      }
    }
  `;
  try {
    await UserClient.mutate({
      mutation: addUserToGroup,
      variables: {
        input: {
          user: userId,
          group: groupId,
          clientMutationId: "",
        },
      },
    });
  } catch (e) {
    ThrowGraphQLError(
      e,
      "already_in_group",
      "User is already assigned to a group"
    );
  }
};

const removeUser = async (userId: string, groupId: string): Promise<void> => {
  const removeUserFromGroup = gql`
    mutation removeUserFromGroup($input: RemoveUserFromGroupInput!) {
      removeUserFromGroup(input: $input) {
        clientMutationId
      }
    }
  `;
  try {
    await UserClient.mutate({
      mutation: removeUserFromGroup,
      variables: {
        input: {
          user: userId,
          group: groupId,
          clientMutationId: "",
        },
      },
    });
  } catch (e) {
    ThrowGraphQLError(e, "already_in_group", "User is not part of this group");
  }
};

export {
  CreateUserGroup,
  GetUserGroup,
  GetUserGroups,
  GetAllUserGroups,
  UpdateUserGroup,
  SetUserGroupParent,
  DeleteUserGroup,
  addUser,
  removeUser,
};
